/**
 * Controles de quem trata a Central, no cabeçalho da conversa:
 * - Tipo: Cliente, Obra, Equipe ou Diretoria (com uma linha explicando cada um).
 * - Ligar ao cliente: busca o pedido pelo número ou pelo nome; as sugestões
 *   do servidor (pelo nome da conversa) aparecem como atalho. Quem confirma
 *   é sempre a pessoa.
 * O PATCH devolve o item da conversa, que sobe para a lista via `aoMudar`.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiLink, FiSearch, FiTag, FiXCircle } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useRespostaMaisRecente, useValorComAtraso } from "../../../hooks/useBuscaRemota";
import { atualizarConversa, buscarClientes } from "../../../services/centralProjetos";
import { ROTULO_CLASSIFICACAO, TIPOS_DE_CONVERSA } from "../apresentacao";
import {
  Ancora,
  AvisoDaLista,
  BotaoCompacto,
  Busca,
  Divisoria,
  FaixaDeSugestao,
  OpcaoDoMenu,
  Popover,
  RotuloDoPopover,
} from "./estilos";
import usePopover from "./usePopover";

const EXPLICACAO_DO_TIPO = Object.freeze({
  cliente: "Conversa com o cliente sobre um pedido",
  obra: "Grupo da obra ou da montagem",
  interno: "Conversa interna da equipe",
  diretoria: "Assuntos da diretoria",
});
const MINIMO_PARA_BUSCAR = 2;

/** PATCH com o toast de sucesso; o erro o `request` já mostra. */
function useAtualizar(conversa, aoMudar) {
  const { request } = useAuth();
  const toast = useToast();
  const [ocupado, setOcupado] = useState(false);

  async function atualizar(corpo, mensagemDeSucesso) {
    setOcupado(true);
    try {
      const item = await atualizarConversa(request, conversa.id, corpo);
      aoMudar(item, corpo);
      toast.success(mensagemDeSucesso);
      return true;
    } catch {
      // O toast do erro já saiu pelo `request`; o popover fica aberto para tentar de novo.
      return false;
    } finally {
      setOcupado(false);
    }
  }

  return { atualizar, ocupado };
}

export function MenuDoTipo({ conversa, aoMudar }) {
  const popover = usePopover();
  const { atualizar, ocupado } = useAtualizar(conversa, aoMudar);
  const classificada = conversa.classificacao && conversa.classificacao !== "desconhecido";
  const rotulo = classificada ? ROTULO_CLASSIFICACAO[conversa.classificacao] : "Classificar";

  async function escolher(tipo) {
    if (tipo === conversa.classificacao) {
      popover.fechar(true);
      return;
    }
    const ok = await atualizar({ classificacao: tipo }, `Conversa marcada como ${ROTULO_CLASSIFICACAO[tipo]}.`);
    if (ok) popover.fechar(true);
  }

  return (
    <Ancora ref={popover.ancora}>
      <BotaoCompacto
        ref={popover.gatilho}
        type="button"
        aria-haspopup="menu"
        aria-expanded={popover.aberto}
        aria-label={`Tipo da conversa: ${rotulo}`}
        title="Tipo da conversa"
        onClick={popover.alternar}
      >
        <FiTag size={15} aria-hidden="true" />
        <span className="rotulo">{rotulo}</span>
        <FiChevronDown size={14} aria-hidden="true" className="rotulo" />
      </BotaoCompacto>
      {popover.aberto ? (
        <Popover $largura={290} role="menu" aria-label="Tipo da conversa">
          <RotuloDoPopover>Tipo da conversa</RotuloDoPopover>
          {TIPOS_DE_CONVERSA.map((tipo) => {
            const atual = tipo === conversa.classificacao;
            return (
              <OpcaoDoMenu
                key={tipo}
                type="button"
                role="menuitemradio"
                aria-checked={atual}
                disabled={ocupado}
                onClick={() => escolher(tipo)}
              >
                {atual ? <FiCheck size={16} aria-hidden="true" /> : <span aria-hidden="true" />}
                <b>{ROTULO_CLASSIFICACAO[tipo]}</b>
                <small>{EXPLICACAO_DO_TIPO[tipo]}</small>
              </OpcaoDoMenu>
            );
          })}
        </Popover>
      ) : null}
    </Ancora>
  );
}

/** Resposta de `clientes-busca`: `{clientes: [{numero_pedido, cliente, etapa}]}` (etapa já é o rótulo). */
function clientesDaResposta(resposta) {
  return Array.isArray(resposta?.clientes) ? resposta.clientes : [];
}

function useBuscaDeClientes(texto) {
  const { request } = useAuth();
  const iniciar = useRespostaMaisRecente();
  const termo = useValorComAtraso(texto.trim());
  const [estado, setEstado] = useState({ termo: "", clientes: [], carregando: false, erro: "" });

  useEffect(() => {
    if (termo.length < MINIMO_PARA_BUSCAR) {
      setEstado({ termo, clientes: [], carregando: false, erro: "" });
      return;
    }
    const vale = iniciar();
    setEstado((atual) => ({ ...atual, termo, carregando: true, erro: "" }));
    buscarClientes(request, termo)
      .then((resposta) => {
        if (vale()) setEstado({ termo, clientes: clientesDaResposta(resposta), carregando: false, erro: "" });
      })
      .catch((erro) => {
        if (vale()) setEstado({ termo, clientes: [], carregando: false, erro: erro?.message || "A busca falhou." });
      });
  }, [termo, request, iniciar]);

  return estado;
}

export function LigarAoCliente({ conversa, aoMudar }) {
  const popover = usePopover();
  const { atualizar, ocupado } = useAtualizar(conversa, aoMudar);
  const [texto, setTexto] = useState("");
  const [ativo, setAtivo] = useState(0);
  const busca = useBuscaDeClientes(popover.aberto ? texto : "");
  const campo = useRef(null);
  const ligada = Boolean(conversa.numero_pedido);
  const buscando = texto.trim().length >= MINIMO_PARA_BUSCAR;

  const opcoes = buscando
    ? busca.clientes.map((c) => ({
        numero: String(c.numero_pedido),
        titulo: [c.numero_pedido, c.cliente].filter(Boolean).join(" · "),
        detalhe: c.etapa || "",
      }))
    : (conversa.sugestoes || []).map((s) => ({
        numero: String(s.numero_pedido),
        titulo: `Ligar ao ${s.numero_pedido}?`,
        detalhe: s.por ? `Pelo nome ${s.por}` : "",
      }));

  useEffect(() => setAtivo(0), [texto, busca.clientes]);
  useEffect(() => {
    // Abriu por um clique: o foco vai para a busca. Fechou: a busca recomeça vazia.
    if (popover.aberto) campo.current?.focus();
    else setTexto("");
  }, [popover.aberto]);

  async function ligar(numero) {
    if (numero === conversa.numero_pedido) {
      popover.fechar(true);
      return;
    }
    const ok = await atualizar({ numero_pedido: numero }, `Conversa ligada ao pedido ${numero}.`);
    if (ok) popover.fechar(true);
  }

  async function desfazer() {
    const ok = await atualizar({ numero_pedido: "" }, "Vínculo desfeito.");
    if (ok) popover.fechar(true);
  }

  function aoTeclar(evento) {
    if (!opcoes.length) return;
    if (evento.key === "ArrowDown") {
      evento.preventDefault();
      setAtivo((i) => (i + 1) % opcoes.length);
    } else if (evento.key === "ArrowUp") {
      evento.preventDefault();
      setAtivo((i) => (i - 1 + opcoes.length) % opcoes.length);
    } else if (evento.key === "Enter") {
      evento.preventDefault();
      ligar(opcoes[ativo].numero);
    }
  }

  // O ícone de vínculo e o subtítulo já dizem "pedido": o botão mostra só o número.
  const rotulo = ligada ? conversa.numero_pedido : "Ligar ao cliente";
  const idDaLista = `clientes-da-conversa-${conversa.id}`;
  return (
    <Ancora ref={popover.ancora}>
      <BotaoCompacto
        ref={popover.gatilho}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={popover.aberto}
        aria-label={ligada ? `Ligada ao pedido ${conversa.numero_pedido}. Trocar` : "Ligar ao cliente"}
        title={ligada ? "Trocar ou desfazer o vínculo" : "Ligar a conversa a um pedido"}
        onClick={popover.alternar}
      >
        <FiLink size={15} aria-hidden="true" />
        <span className="rotulo">{rotulo}</span>
        <FiChevronDown size={14} aria-hidden="true" className="rotulo" />
      </BotaoCompacto>
      {popover.aberto ? (
        <Popover $largura={340} role="dialog" aria-label="Ligar a conversa a um cliente">
          <div style={{ padding: 4 }}>
            <Busca>
              <FiSearch size={16} aria-hidden="true" />
              <input
                ref={campo}
                type="search"
                role="combobox"
                aria-expanded={opcoes.length > 0}
                aria-controls={idDaLista}
                aria-activedescendant={opcoes.length ? `${idDaLista}-${ativo}` : undefined}
                aria-label="Número do pedido ou nome do cliente"
                placeholder="Número do pedido ou nome do cliente"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={aoTeclar}
              />
            </Busca>
          </div>
          {opcoes.length ? (
            <RotuloDoPopover>{buscando ? "Pedidos" : "Sugestões pelo nome"}</RotuloDoPopover>
          ) : null}
          <div id={idDaLista} role="listbox" aria-label="Pedidos">
            {opcoes.map((opcao, i) => (
              <OpcaoDoMenu
                key={opcao.numero}
                id={`${idDaLista}-${i}`}
                type="button"
                role="option"
                aria-selected={i === ativo}
                $destaque={i === ativo}
                disabled={ocupado}
                onMouseEnter={() => setAtivo(i)}
                onClick={() => ligar(opcao.numero)}
              >
                {opcao.numero === conversa.numero_pedido ? <FiCheck size={16} aria-hidden="true" /> : <span aria-hidden="true" />}
                <b>{opcao.titulo}</b>
                {opcao.detalhe ? <small>{opcao.detalhe}</small> : null}
              </OpcaoDoMenu>
            ))}
          </div>
          {buscando && busca.carregando && !opcoes.length ? <AvisoDaLista as="div" style={{ padding: 14 }}>Buscando…</AvisoDaLista> : null}
          {buscando && !busca.carregando && busca.termo === texto.trim() && !opcoes.length ? (
            <AvisoDaLista as="div" style={{ padding: 14 }}>{busca.erro || "Nenhum pedido encontrado."}</AvisoDaLista>
          ) : null}
          {!buscando && !opcoes.length ? (
            <AvisoDaLista as="div" style={{ padding: 14 }}>Digite o número do pedido ou parte do nome do cliente.</AvisoDaLista>
          ) : null}
          {ligada ? (
            <>
              <Divisoria />
              <OpcaoDoMenu type="button" disabled={ocupado} onClick={desfazer}>
                <FiXCircle size={16} aria-hidden="true" />
                <b>Desfazer vínculo</b>
                <small>A conversa deixa de aparecer na ficha do pedido {conversa.numero_pedido}</small>
              </OpcaoDoMenu>
            </>
          ) : null}
        </Popover>
      ) : null}
    </Ancora>
  );
}

/** Faixa sob o cabeçalho: os pedidos que o servidor sugere pelo nome da conversa. */
export function SugestoesDeVinculo({ conversa, aoMudar }) {
  const { atualizar, ocupado } = useAtualizar(conversa, aoMudar);
  const sugestoes = conversa.sugestoes || [];
  if (conversa.numero_pedido || !sugestoes.length) return null;
  return (
    <FaixaDeSugestao>
      <span>
        <FiLink size={14} aria-hidden="true" /> Pelo nome, pode ser do pedido
      </span>
      {sugestoes.slice(0, 3).map((s) => (
        <BotaoCompacto
          key={s.numero_pedido}
          type="button"
          style={{ height: 30 }}
          disabled={ocupado}
          title={s.por ? `Pelo nome ${s.por}` : undefined}
          onClick={() => atualizar({ numero_pedido: String(s.numero_pedido) }, `Conversa ligada ao pedido ${s.numero_pedido}.`)}
        >
          Ligar ao {s.numero_pedido}?
        </BotaoCompacto>
      ))}
    </FaixaDeSugestao>
  );
}
